import React, { useEffect, useState } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";
import { Loader2, PencilLine, SlashIcon, Trash2 } from "lucide-react";
import "../Styles/Results.css";
import { Button } from "./ui/button";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { useAuth } from "../context/AuthContext";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "./ui/alert-dialog";
import { useProjectId } from "../context/ProjectIdContext";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import { useProjectInfo } from "../context/ProjectInfoContext";
import { UnitSystemContext } from "../context/UnitSystemContext";
import { useContext } from "react";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

const Results = () => {
  // const location = useLocation();
  const navigate = useNavigate();
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  // const projectId = location.state?.projectId;

  const { projectData } = useProjectInfo();
  const { projectId } = useProjectId();

  // console.log(projectId)

  const [data, setData] = useState([]); // State to store fetched data
  const [constants, setConstants] = useState([]); // State to store constants
  const [constantsv, setConstantsV] = useState([]); // State to store constants
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage] = useState("Design Summary");
  const { userSession } = useAuth(); // Get session and logout from context
  const [deleteId, setDeleteId] = useState(null);
  const [toastList, setToastList] = useState([]);
  const { unitSystem } = useContext(UnitSystemContext);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // const [fetchedProjectId, setFetchedProjectId] = useState("");

  const [projectDetails, setProjectDetails] = useState({
    title: "",
    location: "",
    clientName: "",
    sippInstaller: "",
    sippCalcDesignDate: "",
  });

  const userType = userSession.userType;

  // Fetch data from the API
  useEffect(() => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials); // Base64 encode the credentials

    const fetchData = async () => {
      if (userType !== "applicator") return; // Ensure only "applicator" calls this
      if (!projectId) return; // Ensure projectId is provided for applicator

      try {
        const response = await fetch(`${API_URL}/api/project/${projectId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          },
        });

        if (response.ok) {
          const result = await response.json();
          // console.log(result)

          setConstants(result.data.data.constants);

          setConstantsV(result.data.data);

          setData(result.data.data.calculations); // Update state with fetched data
          // console.log(result.data.data.calculations);

          //  // Filter calculations to match the current project
          //  const filteredData = result.data.calculations.filter(
          //   (item) => item.project === projectId
          // );
          // setData(filteredData);

          // const projectId = result.data.calculations[0].project;

          // console.log(projectId)

          // Extract the projectId from the calculations
          // const projectId = result.data.calculations.find(item => item.project)?.project;

          // Store the projectId for later use
          // if (projectId) {
          //   setFetchedProjectId(projectId);
          // } else {
          //   console.warn("No projectId found in the API response.");
          // }
          // console.log(fetchedProjectId)
        } else {
          setError("Failed to fetch results. Please Sign in and try again.");
          console.error("Error fetching results:", response.statusText);
        }
      } catch (error) {
        setError("An error occurred while fetching results.");
        console.error("Error with fetch:", error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchCustomerData = async () => {
      if (userType !== "customer") return; // Ensure only "customer" calls this

      try {
        const token = localStorage.getItem("x-auth-token");
        const response = await fetch(`${API_URL}/api/calculate`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${encodedCredentials}`,
            "x-auth-token": token,
          },
        });

        if (response.ok) {
          const result = await response.json();
          setData(result.data.calculations);
        } else {
          setError("Failed to fetch results. Please Sign in and try again.");
          console.error("Error fetching results:", response.statusText);
        }
      } catch (error) {
        setError("An error occurred while fetching results.");
        console.error("Error with fetch:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (userType === "applicator") {
      fetchData();
    } else if (userType === "customer") {
      fetchCustomerData();
    }
  }, [projectId, userType]);

  // console.log(constantsv);

  // Fetching Project details
  useEffect(() => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials); // Base64 encode the credentials

    // if (!fetchedProjectId) {
    //   console.warn("fetchProjectDetails skipped: No project ID provided yet.");
    //   return;
    // }

    if (userType === "applicator") {
      const fetchProjectDetails = async () => {
        if (!projectId) {
          console.error("No project ID provided.");
          return;
        }

        try {
          const response = await fetch(`${API_URL}/api/project/${projectId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
            },
          });

          if (response.ok) {
            const result = await response.json();
            const project = result.data.data.project;

            // console.log("Project details fetched:", project);

            // Update state with fetched project details
            setProjectDetails({
              title: project.title,
              location: project.location,
              clientName: project.clientName,
              sippInstaller: project.sippInstaller,
              sippCalcDesignDate: project.sippCalcDesignDate,
            });
          } else {
            console.error(
              "Failed to fetch project details:",
              response.statusText,
            );
          }
        } catch (error) {
          console.error("Error fetching project details:", error);
        }
      };

      fetchProjectDetails();
    }
  }, [projectId, userType]); // Only runs when the `projectId` changes

  const handleSaveAll = async () => {
    const ids = data.map((item) => item._id);

    try {
      const token = localStorage.getItem("x-auth-token");
      const response = await fetch(`${API_URL}/api/calculate/keep`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`,
          "x-auth-token": token,
        },
        body: JSON.stringify({ calculationIds: ids }),
      });

      const result = await response.json();

      if (response.ok) {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "SUCCESS!",
            description:
              result.data?.message ||
              result?.error ||
              "All calculations saved successfully!",
            variant: "default",
          },
        ]);
      } else {
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              result.data?.message ||
              result?.error ||
              result?.error ||
              "Failed to save calculations. Please try again.",
            variant: "destructive",
          },
        ]);

        console.error("Error saving calculations:", response.statusText);
      }
    } catch (error) {
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
      console.error("Error with save:", error);
    }
  };

  const handleReview = (requestData, id) => {
    localStorage.setItem("savedUnitSystem", unitSystem); // Save current unit system
    navigate("/calculator", {
      state: {
        projectId: projectId,
        requestData: {
          ...requestData,
          performRecalculation: true, //  Set recalculation mode
          existingCalculationId: id, // Include existing calculation ID
        },
      },
    });
  };

  const handleAddNewDesign = () => {
    // localStorage.setItem("savedUnitSystem", unitSystem); // Save current unit system
    navigate("/calculator"); // Navigate to Calculator
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      document.activeElement?.blur(); // Prevent focus error
      handleDelete(deleteId); // Delete the item
    }
    setIsDialogOpen(false); // Close the dialog
    setTimeout(() => setDeleteId(null), 200); // Reset deleteId safely
  };

  const handleCancelDelete = () => {
    setIsDialogOpen(false); // Close the dialog
    setTimeout(() => setDeleteId(null), 200); // Reset deleteId safely
  };

  // const handleReviewAndDelete = async (requestData, id) => {
  //   // Navigate to the calculator page
  //   navigate("/calculator", {
  //     state: {
  //       projectId: projectId,
  //       requestData: {
  //         workingPressure: requestData.workingPressure,
  //         soilDensity: requestData.soilDensity,
  //         modulusOfSoilRtn: requestData.modulusOfSoilRtn,
  //         ...requestData,
  //       },
  //     },
  //   });

  //   // Perform deletion
  //   if (!id) return;

  //   try {
  //     const token = localStorage.getItem("x-auth-token"); // Retrieve token
  //     const response = await fetch(`${API_URL}/api/calculate/${id}`, {
  //       method: "DELETE",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
  //         "x-auth-token": token,
  //       },
  //     });

  //     if (response.ok) {
  //       setData((prevData) => prevData.filter((item) => item._id !== id));
  //       setDeleteId(null); // Clear the ID after deletion
  //     } else {
  //       console.error("Failed to delete the item.");
  //     }
  //   } catch (err) {
  //     console.error("Error during deletion:", err);
  //   }
  // };

  const handleDelete = async (id) => {
    if (!id) return;

    try {
      const token = localStorage.getItem("x-auth-token"); // Retrieve token
      const response = await fetch(`${API_URL}/api/calculate/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "x-auth-token": token,
        },
      });

      if (response.ok) {
        setData((prevData) => prevData.filter((item) => item._id !== id));
        setDeleteId(null); // Clear the ID after deletion
      } else {
        console.error("Failed to delete the item.");
      }
    } catch (err) {
      console.error("Error during deletion:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
        <Loader2 className="tw-animate-spin tw-h-10 tw-w-10 tw-text-[#00B5ED]" />
      </div>
    );
  }

  if (error) {
    return <div className="tw-mt-44 tw-h-screen">{error}</div>;
  }

  if (!data) {
    return <div className="tw-mt-44 tw-h-screen">No results to display.</div>;
  }

  const breadcrumbs = [
    {
      label: "Project Information",
      link: "/projectinfo",
      isCurrent: currentPage === "ProjectInfo",
    },
    {
      label: "Calculator",
      link: "/calculator",
      isCurrent: currentPage === "Calculator",
    },
    {
      label: "Design Summary",
      link: "/designsummary",
      isCurrent: currentPage === "Design Summary",
    },
  ];

  const onPrint = (e) => {
    e.preventDefault();
    window.print();
  };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  const unitKey = unitSystem === "imperial" ? "inImperial" : "inSi";

  const getValueByUnit = (obj) => {
    if (!obj) return "NULL";

    // handle API inconsistency
    if (unitKey === "inSi") {
      return obj.inSi ?? obj.inSI ?? "NULL";
    }

    return obj[unitKey] ?? "NULL";
  };

  const selectedResin = constantsv?.calculations?.[0]?.requestData?.resin;

  const resinKey = selectedResin
    ? `resin${selectedResin.replace("R", "")}`
    : null;

  const getResinValue = (property) => {
    if (!constants || !resinKey) return "NULL";

    const resin = constants?.[resinKey];
    if (!resin) return "NULL";

    const prop = resin?.[property];
    if (!prop) return "NULL";

    if (unitKey === "inSi") {
      return prop.inSi ?? prop.inSI ?? "NULL";
    }

    return prop[unitKey] ?? "NULL";
  };

  return (
    <div className="tw-mt-28 md:tw-mt-44 tw-w-full">
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb>
          <BreadcrumbList>
            {breadcrumbs.map((breadcrumb, index) => (
              <React.Fragment key={index}>
                <BreadcrumbItem>
                  {breadcrumb.isCurrent ? (
                    // Render the current page without a link
                    <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
                  ) : (
                    // Render the previous pages without links
                    <BreadcrumbLink>{breadcrumb.label}</BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < breadcrumbs.length - 1 && (
                  <BreadcrumbSeparator>
                    <SlashIcon />
                  </BreadcrumbSeparator>
                )}
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <h1 className="tw-flex tw-text-base md:tw-text-lg tw-font-bold tw-mb-10 md:tw-mb-10 tw-justify-center tw-underline">
        Design Summary
      </h1>

      {/* Change to Information from backend */}
      {userType === "applicator" && (
        <div className="tw-my-6">
          <ul>
            <li>
              <span className="tw-font-semibold tw-text-[#00B5ED]">
                Project Title:
              </span>{" "}
              {projectDetails.title}
            </li>
            <li>
              <span className="tw-font-semibold tw-text-[#00B5ED]">
                Project Location:
              </span>{" "}
              {projectDetails.location}
            </li>
            <li>
              <span className="tw-font-semibold tw-text-[#00B5ED]">
                Client:
              </span>{" "}
              {projectDetails.clientName}
            </li>
            <li>
              <span className="tw-font-semibold tw-text-[#00B5ED]">
                SIPP Installer:
              </span>{" "}
              {projectDetails.sippInstaller}
            </li>
            <li>
              <span className="tw-font-semibold tw-text-[#00B5ED]">
                SIPP Calc Design Date:
              </span>{" "}
              {projectDetails.sippCalcDesignDate}
            </li>
          </ul>
        </div>
      )}

      <div className="tw-flex tw-justify-end">
        <Link
          to={{
            pathname: "/calculator",
            state: {
              resin: projectData.resin,
            },
          }}
        >
          <Button
            onClick={handleAddNewDesign}
            variant="default"
            className="tw-flex tw-justify-end"
            size="lg"
          >
            <div className="tw-font-semibold tw-text-lg ">Add New Design</div>
          </Button>
        </Link>
      </div>

      <h1 className="tw-flex tw-text-base md:tw-text-lg tw-font-bold tw-mb-10 md:tw-mb-10 tw-justify-center tw-underline">
        Design Paramaters
      </h1>

      <div className="tw-flex tw-justify-start tw-space-x-20">
        {/* Constants */}
        {userType === "applicator" && (
          <div className="tw-my-6">
            <ul>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  System Working Pressure, pw:
                </span>{" "}
                {getValueByUnit(constants?.systemWorkingPressure)}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Soil density, w:
                </span>{" "}
                {getValueByUnit(constants?.soilDensity)}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Modulus of soil rtn, E's:
                </span>{" "}
                {getValueByUnit(constants?.modulusOfSoil)}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Enhancement Factor:
                </span>{" "}
                {constants?.enhancementFactor}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Resin:
                </span>{" "}
                {selectedResin?.replace("resin", "")}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  D790 Short-term Value Flex Mod, E:
                </span>{" "}
                {getResinValue("E")}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  D790 Short-term Value Flex Strength, σ:
                </span>{" "}
                {getResinValue("phi")}
              </li>
            </ul>
          </div>
        )}

        {/* Calculation Constants */}

        {userType === "applicator" && (
          <div className="tw-my-6">
            <ul>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Ovality:
                </span>{" "}
                {constants?.ovality}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Factor of Safety:
                </span>{" "}
                {constants?.factorOfSafety}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Poisson Ratio:
                </span>{" "}
                {constants?.poissonRatio}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Allowable Stress:
                </span>{" "}
                {getValueByUnit(constants?.allowableStress)}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Resiline Design FLexural Retention Factor, CRFf:
                </span>{" "}
                {constants?.resinProperties?.CRFf}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  Resiline Design Tensile Retention Factor, CRFt:
                </span>{" "}
                {constants?.resinProperties?.CRFt}
              </li>
              <li>
                <span className="tw-font-semibold tw-text-[#00B5ED]">
                  D638 Short-term X1.7 Tensile Strength, s:
                </span>{" "}
                {getResinValue("S")}
              </li>
            </ul>
          </div>
        )}
      </div>
      {/* 
      <div className="tw-flex tw-justify-center tw-gap-2 tw-text-center">
        <span className="tw-font-semibold tw-text-[#00B5ED]">Resin:</span>{" "}
        {constantsv?.calculations[0]?.requestData?.resin}
        {constants?.resinProperties && (
          <div className="">
            [{" "}
            {Object.entries(constants.resinProperties).map(
              ([key, value], index, arr) => (
                <span key={key}>
                  <span className="tw-text-[#00B5ED]">{key}</span>: {value}
                  {index < arr.length - 1 && ", "}
                </span>
              ),
            )}{" "}
            ]
          </div>
        )}
      </div> */}

      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <div className="tw-flex tw-items-center tw-justify-evenly md:tw-justify-end tw-space-x-8 tw-my-10 tw-w-full">
          {/* <Link to="/projectlist">
            <Button variant="default" className="">
              <div className="tw-font-semibold tw-text-sm">Your Projects</div>
            </Button>
          </Link> */}

          <Button variant="default" className="" onClick={onPrint}>
            <div className="tw-font-semibold tw-text-sm">Print Summary</div>
          </Button>
        </div>
      </div>

      {/* <div className="tw-flex tw-items-center tw-justify-end">
        <Button
          variant="default"
          className="tw-shadow-none tw-items-center tw-font-semibold  tw-bg-green-500 hover:tw-bg-green-600 tw-mb-4"
          onClick={handleSaveAll}
        >
          <FolderCheck className="tw-h-6 tw-w-6 tw-mr-1 tw-mb-0.5" />
          Save All
        </Button>
      </div> */}

      {/* Class I Design Table */}
      <div className="tw-mb-12">
        <h2 className="tw-text-sm md:tw-text-base tw-font-bold tw-bg-[#00B5ED] tw-text-white tw-p-2 tw-uppercase tw-mb-2 tw-rounded-t-md">
          Class I Design
        </h2>
        <Table className="tw-pb-4 tw-border-collapse tw-border tw-border-black">
          <TableHeader>
            <TableRow className="tw-border tw-border-black">
              <TableHead className="tw-border tw-border-black">
                Pipe Section No.
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe ID (in)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                SIPP Liner Length (ft)
              </TableHead>

              {/* <TableHead className="tw-border tw-border-black">
                t for X1.6 (mm)
              </TableHead> */}
              <TableHead className="tw-border tw-border-black">
                Design t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Install t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Short-term Burst Pressure (psi)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design Assumes
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index} className="tw-border tw-border-black">
                <TableCell className="tw-border tw-border-black">
                  {index + 1}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.internalDiameter ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.lengthLined ?? "NULL"}
                </TableCell>

                {/* <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.tX1_6 ?? "NULL"}
                </TableCell> */}
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassI?.designTClassI ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassI?.installTClassI ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassI?.burstPressureI ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.vacuumCondition ?? "NULL"}
                </TableCell>
                <TableCell className="">
                  <div className="tw-flex tw-space-x-2 tw-justify-center">
                    {/* Review Button */}
                    <div className="tw-inline-block">
                      <button
                        onClick={() => handleReview(item.requestData, item._id)}
                        className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-[#00B5ED] tw-shadow-sm hover:tw-bg-primary/90"
                      >
                        <PencilLine className="tw-w-4 tw-h-4 tw-font-bold" />
                      </button>
                    </div>

                    {/* Delete Button */}
                    <AlertDialog
                      key={item._id}
                      open={isDialogOpen && deleteId === item._id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setTimeout(() => {
                            setDeleteId(null);
                          }, 200);
                        }
                        setIsDialogOpen(open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsDialogOpen(true);
                          }}
                          className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-destructive tw-shadow-sm hover:tw-bg-destructive/90"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-font-bold" />
                        </button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this result
                            permanently? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel
                            className="tw-border-2"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </AlertDialogCancel>

                          <AlertDialogAction
                            className="tw-bg-destructive mb-2"
                            onClick={handleConfirmDelete}
                          >
                            Confirm
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="tw-text-xs tw-border-x tw-border-b tw-p-4 tw-border-black">
          <strong>NOTES:</strong>
          <ul>
            <li>
              Liner thickness is based on ASTM F1216 Non-Mandatory Design
              Appendix X1.
            </li>
            <li>
              Class I design is to bridge a 1 inch (25 mm) max circular hole in
              the pipe wall.
            </li>
            <li>Max. Liner thickness for NSF61 is 0.55 inch (14 mm).</li>
            {/* <li>
              Final resin volume includes 10% allowance and approximate number
              of drums reported
            </li> */}
          </ul>
        </p>
      </div>

      {/* Class II Design Table */}
      <div className="tw-mb-12">
        <h2 className="tw-text-sm md:tw-text-base tw-font-bold tw-bg-[#00B5ED] tw-text-white tw-p-2 tw-uppercase tw-mb-2 tw-rounded-t-md">
          Class II Design
        </h2>
        <Table className="tw-pb-4 tw-border-collapse tw-border tw-border-black">
          <TableHeader>
            <TableRow className="tw-border tw-border-black">
              <TableHead className="tw-border tw-border-black">
                Pipe Section No.
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe ID (in)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                SIPP Liner Length (ft)
              </TableHead>

              <TableHead className="tw-border tw-border-black">
                t for X1.6 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Install t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Short-term Burst Pressure (psi)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design Assumes
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index} className="tw-border tw-border-black">
                <TableCell className="tw-border tw-border-black">
                  {index + 1}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.internalDiameter ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.lengthLined ?? "NULL"}
                </TableCell>

                <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.tX1_6 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.designTClassII ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.installTClassII ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.addedClassII.burstPressureII ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.vacuumCondition ?? "NULL"}
                </TableCell>
                <TableCell className="">
                  <div className="tw-flex tw-space-x-2 tw-justify-center">
                    {/* Review Button */}
                    <div className="tw-inline-block">
                      <button
                        onClick={() => handleReview(item.requestData, item._id)}
                        className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-[#00B5ED] tw-shadow-sm hover:tw-bg-primary/90"
                      >
                        <PencilLine className="tw-w-4 tw-h-4 tw-font-bold" />
                      </button>
                    </div>

                    {/* Delete Button */}
                    <AlertDialog
                      key={item._id}
                      open={isDialogOpen && deleteId === item._id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setTimeout(() => {
                            setDeleteId(null);
                          }, 200);
                        }
                        setIsDialogOpen(open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsDialogOpen(true);
                          }}
                          className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-destructive tw-shadow-sm hover:tw-bg-destructive/90"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-font-bold" />
                        </button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this result
                            permanently? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel
                            className="tw-border-2"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </AlertDialogCancel>

                          <AlertDialogAction
                            className="tw-bg-destructive mb-2"
                            onClick={handleConfirmDelete}
                          >
                            Confirm
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="tw-text-xs tw-border-x tw-border-b tw-p-4 tw-border-black">
          <strong>NOTES:</strong>
          <ul>
            <li>
              Liner thickness is based on ASTM F1216 Non-Mandatory Design
              Appendix X1.
            </li>
            <li>
              Class II design is to bridge a 1 inch (25 mm) max circular hole in
              the pipe wall.
            </li>
            <li>Max. Liner thickness for NSF61 is 0.55 inch (14 mm).</li>
            {/* <li>
              Final resin volume includes 10% allowance and approximate number
              of drums reported
            </li> */}
          </ul>
        </p>
      </div>

      {/* Class III Design Table */}
      <div className="tw-mb-12">
        <h2 className="tw-text-sm md:tw-text-base tw-font-bold tw-bg-[#00B5ED] tw-text-white tw-p-2 tw-uppercase tw-mb-2 tw-rounded-t-md">
          Class III Design
        </h2>
        <Table className="tw-pb-4 tw-border-collapse tw-border tw-border-black">
          <TableHeader>
            <TableRow className="tw-border tw-border-black">
              <TableHead className="tw-border tw-border-black">
                Pipe Section No.
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe ID (in)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                SIPP Liner Length (ft)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.1 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.6/X1.7 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Install t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Short-term Burst Pressure (psi)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design Assumes
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index} className="tw-border tw-border-black">
                <TableCell className="tw-border tw-border-black">
                  {index + 1}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.internalDiameter ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.lengthLined ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.tX1_1 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.tX1_6 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.partiallyDesignThickness ??
                    "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.partiallyInstallThickness ??
                    "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.partiallyDeteriorated.burstPressureIII ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.vacuumCondition ?? "NULL"}
                </TableCell>
                <TableCell className="">
                  <div className="tw-flex tw-space-x-2 tw-justify-center">
                    {/* Review Button */}
                    <div className="tw-inline-block">
                      <button
                        onClick={() => handleReview(item.requestData, item._id)}
                        className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-[#00B5ED] tw-shadow-sm hover:tw-bg-primary/90"
                      >
                        <PencilLine className="tw-w-4 tw-h-4 tw-font-bold" />
                      </button>
                    </div>

                    {/* Delete Button */}
                    <AlertDialog
                      key={item._id}
                      open={isDialogOpen && deleteId === item._id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setTimeout(() => {
                            setDeleteId(null);
                          }, 200);
                        }
                        setIsDialogOpen(open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsDialogOpen(true);
                          }}
                          className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-destructive tw-shadow-sm hover:tw-bg-destructive/90"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-font-bold" />
                        </button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this result
                            permanently? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel
                            className="tw-border-2"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </AlertDialogCancel>

                          <AlertDialogAction
                            className="tw-bg-destructive mb-2"
                            onClick={handleConfirmDelete}
                          >
                            Confirm
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="tw-text-xs tw-border-x tw-border-b tw-p-4 tw-border-black">
          <strong>NOTES:</strong>
          <ul>
            <li>
              Liner thickness is based on ASTM F1216 Non-Mandatory Design
              Appendix X1.
            </li>
            <li>
              Class III design is to bridge a 1 inch (25 mm) max circular hole
              in the pipe wall.
            </li>
            <li>Max. Liner thickness for NSF61 is 0.55 inch (14 mm).</li>
            {/* <li>
              Final resin volume includes 10% allowance and approximate number
              of drums reported
            </li> */}
          </ul>
        </p>
      </div>

      {/* Class IV Design Table */}
      <div className="tw-mb-6">
        <h2 className="tw-text-sm md:tw-text-base tw-font-bold tw-bg-[#00B5ED] tw-text-white tw-p-2 tw-uppercase tw-mb-2 tw-rounded-t-md">
          Class IV Design
        </h2>
        <Table className="tw-pb-4 tw-border-collapse tw-border tw-border-black">
          <TableHeader>
            <TableRow className="tw-border tw-border-black">
              <TableHead className="tw-border tw-border-black">
                Pipe Section No.
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Pipe ID (in)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                SIPP Liner Length (ft)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.1 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.3 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                t for X1.7 (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Install t (mm)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Short-term Burst Pressure (psi)
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Design Assumes
              </TableHead>
              <TableHead className="tw-border tw-border-black">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((item, index) => (
              <TableRow className="tw-border tw-border-black" key={index}>
                <TableCell className="tw-border tw-border-black">
                  {index + 1}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.internalDiameter ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.lengthLined ?? "NULL"}
                </TableCell>
                <TableCell
                  className={`tw-border tw-border-black ${
                    item.fullyDeteriorated.tX1_1 > 14 ? "tw-bg-[#FFBF00]" : ""
                  }`}
                >
                  {item.fullyDeteriorated.tX1_1 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.tX1_3 ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.tX1_7 ?? "NULL"}
                </TableCell>
                <TableCell
                  className={`tw-border tw-border-black ${
                    item.fullyDeteriorated.fullyDesignThickness > 14
                      ? "tw-bg-[#FFBF00] tw-p-2"
                      : ""
                  }`}
                >
                  {item.fullyDeteriorated.fullyDesignThickness ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.fullyInstallThickness ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.fullyDeteriorated.burstPressureIV ?? "NULL"}
                </TableCell>
                <TableCell className="tw-border tw-border-black">
                  {item.vacuumCondition ?? "NULL"}
                </TableCell>
                <TableCell className="">
                  <div className="tw-flex tw-space-x-2 tw-justify-center">
                    {/* Review Button */}
                    <div className="tw-inline-block">
                      <button
                        onClick={() => handleReview(item.requestData, item._id)}
                        className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-[#00B5ED] tw-shadow-sm hover:tw-bg-primary/90"
                      >
                        <PencilLine className="tw-w-4 tw-h-4 tw-font-bold" />
                      </button>
                    </div>

                    {/* Delete Button */}
                    <AlertDialog
                      key={item._id}
                      open={isDialogOpen && deleteId === item._id}
                      onOpenChange={(open) => {
                        if (!open) {
                          setTimeout(() => {
                            setDeleteId(null);
                          }, 200);
                        }
                        setIsDialogOpen(open);
                      }}
                    >
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={() => {
                            setDeleteId(item._id);
                            setIsDialogOpen(true);
                          }}
                          className="tw-text-white tw-py-1 tw-px-4 tw-text-xs tw-font-semibold tw-bg-destructive tw-shadow-sm hover:tw-bg-destructive/90"
                        >
                          <Trash2 className="tw-w-4 tw-h-4 tw-font-bold" />
                        </button>
                      </AlertDialogTrigger>

                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this result
                            permanently? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>

                        <AlertDialogFooter>
                          <AlertDialogCancel
                            className="tw-border-2"
                            onClick={handleCancelDelete}
                          >
                            Cancel
                          </AlertDialogCancel>

                          <AlertDialogAction
                            className="tw-bg-destructive mb-2"
                            onClick={handleConfirmDelete}
                          >
                            Confirm
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="tw-text-xs tw-border-x tw-border-b tw-p-4 tw-border-black">
          <strong>NOTES:</strong>
          <ul>
            <li>
              Liner thickness is based on ASTM F1216 Non-Mandatory Design
              Appendix X1.
            </li>
            <li>
              Class IV is only to meet the pipe operational pressure
              requirements.
            </li>
            <li>Max. Liner thickness for NSF61 is 0.55 inch (14 mm).</li>
            {/* <li>
              Final resin volume includes 10% allowance and approximate number
              of drums reported
            </li> */}
          </ul>
        </p>
      </div>

      {userType === "applicator" && (
        <div className="tw-flex tw-justify-end tw-w-full tw-mb-10 md:tw-mb-20">
          <Link to="/nextsteps2">
            <Button variant="default" onClick={handleSaveAll} className="">
              <div className="tw-font-semibold tw-text-sm">Proceed</div>
            </Button>
          </Link>
        </div>
      )}

      {/* <div className="result-group tw-rounded-b-[5px] tw-overflow-hidden tw-mt-8">
        <h3 className="tw-text-sm md:tw-text-base">
          CLASS III RESIN VOLUME REQUIRED
        </h3>
        <table>
          <tbody>
            <tr>
              <td>External Dia.</td>
              <td>{data.class_iii_resin.external_dia}</td>
              <td>in</td>
            </tr>
            <tr>
              <td>Internal Dia.</td>
              <td>{data.class_iii_resin.internal_dia}</td>
              <td>in</td>
            </tr>
            <tr>
              <td>Liner Area</td>
              <td>{data.class_iii_resin.liner_area}</td>
              <td>in^2</td>
            </tr>
            <tr>
              <td>Liner Volume</td>
              <td>{data.class_iii_resin.liner_volume}</td>
              <td>ft^3</td>
            </tr>
            <tr>
              <td>Drums of Resin</td>
              <td>{data.class_iii_resin.drums}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="result-group tw-rounded-b-[5px] tw-overflow-hidden tw-mt-8">
        <h3 className="tw-text-sm md:tw-text-base">
          CLASS IV RESIN VOLUME REQUIRED
        </h3>
        <table>
          <tbody>
            <tr>
              <td>External Dia.</td>
              <td>{data.class_iv_resin?.external_dia}</td>
              <td>in</td>
            </tr>
            <tr>
              <td>Internal Dia.</td>
              <td>{data.class_iv_resin?.internal_dia}</td>
              <td>in</td>
            </tr>
            <tr>
              <td>Liner Area</td>
              <td>{data.class_iv_resin?.liner_area}</td>
              <td>in^2</td>
            </tr>
            <tr>
              <td>Liner Volume</td>
              <td>{data.class_iv_resin?.liner_volume}</td>
              <td>ft^3</td>
            </tr>
            <tr>
              <td>Drums of Resin</td>
              <td>{data.class_iv_resin?.drums}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div> */}

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </div>
  );
};

export default Results;
